import { createAsyncThunk } from '@reduxjs/toolkit';

import { ProductPackage } from '../../../../common/types/Entities';
import { APISuiteGet } from '../ApplicationSuites/ApplicationSuites.api';
import { APITenantGet } from '../Tenants/Tenants.api';

import {
    APIProductPackageDelete,
    APIProductPackageGetSingle,
    APIProductPackagePost,
    APIProductPackagePostParams,
    APIProductPackagePostResponse,
    APIProductPackagePut,
    APIProductPackagePutParams,
    APIProductPackagePutResponse,
    APIProductPackagesGetList
} from './ProductPackages.api';

export type FetchProductPackageDetailsResponse = {
    id: string;
    name: string;
    packageNote: string;
    tenantId: string;
    productPackageId: string;
    applicationSuiteId: string;
    applicationSuiteName: string;
    tenantName: string;
    tenantRequiresLicense: boolean;
};

export const fetchProductPackageDetails = createAsyncThunk(
    'productPackages/fetchProductPackageDetails',
    async (item: { tenantId: string; productPackageId: string; applicationSuiteId: string; signal?: AbortSignal }): Promise<FetchProductPackageDetailsResponse> => {
        const [suiteResponse, tenantResponse, packageResponse] = await Promise.all([
            APISuiteGet({ id: item.applicationSuiteId }),
            APITenantGet({ id: item.tenantId }),
            APIProductPackageGetSingle({ tenantId: item.tenantId, productPackageId: item.productPackageId }),
        ]);
        const tenant = (tenantResponse).data;
        const { productPackageId, packageName, packageNote } = packageResponse.data;
        return {
            id: productPackageId,
            name: packageName,
            packageNote,
            tenantId: item.tenantId,
            productPackageId,
            applicationSuiteId: suiteResponse.data.id,
            applicationSuiteName: suiteResponse.data.name,
            tenantName: tenant.name,
            tenantRequiresLicense: tenant.isLicenseRequired,
        };
    }
);

export const fetchProductPackages = createAsyncThunk('productPackages/fetchProductPackages', async (item: { tenantId: string, signal?: AbortSignal }) => {
    const response = await APIProductPackagesGetList(item);
    return formatProductPackages(response.data);
});

export const postProductPackage = createAsyncThunk('productPackages/postProductPackage', async (item: APIProductPackagePostParams) => {
    const response = await APIProductPackagePost(item);
    const getRows = await APIProductPackagesGetList({ tenantId: item.tenantId });
    const rows = formatProductPackages(getRows.data);
    return {
        item,
        response,
        rows
    };
});

export type PostProductPackageThunkResponse = {
    item: APIProductPackagePostParams,
    response: APIProductPackagePostResponse,
    rows: ProductPackage[]
};

export const putProductPackage = createAsyncThunk('productPackages/putProductPackage', async (item: APIProductPackagePutParams) => {
    const response = await APIProductPackagePut(item);
    const getRows = await APIProductPackagesGetList({ tenantId: item.tenantId });
    const rows = formatProductPackages(getRows.data);
    return {
        item,
        response,
        rows
    };
});

export const DeleteProductPackage = createAsyncThunk('productPackages/deleteProductPackage', async (item: { tenantId: string; productPackageId: string }) => {
    const response = await APIProductPackageDelete(item);
    const getRows = await APIProductPackagesGetList({ tenantId: item.tenantId });
    const rows = formatProductPackages(getRows.data);
    return {
        item,
        response,
        rows
    };
});

export type PutProductPackageThunkResponse = {
    item: APIProductPackagePutParams,
    response: APIProductPackagePutResponse,
    rows: ProductPackage[]
};

const formatProductPackages = (rows: ProductPackage[]) => {
    return rows.map((item) => ({
        'id': `${item.tenantId}.${item.productPackageId}`,
        'name': item.packageName,
        ...item,
    }));
};